import { useEffect, useMemo, useState } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { analysisApi } from '../api/client';
import TradeStatisticsCard from '../components/analysis/TradeStatisticsCard';
import { brokerBadgeClass, brokerLabel } from '../utils/broker';
import { formatCurrency, formatDate, formatPercent, formatProfitFactor } from '../utils/formatters';

const POSITION_OPTIONS = [
  { value: 'open', label: 'Open Positions' },
  { value: 'closed', label: 'Closed Positions' },
];

const TH_STYLE = {
  textAlign: 'left',
  padding: '0.66rem 0.78rem',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: '#f4f8ff',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '0.66rem 0.78rem',
  fontSize: '0.82rem',
  whiteSpace: 'nowrap',
};

const TH_CENTER_STYLE = {
  ...TH_STYLE,
  textAlign: 'center',
};

const TD_CENTER_STYLE = {
  ...TD_STYLE,
  textAlign: 'center',
};

function toNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function formatSignedCurrency(value) {
  if (value === undefined || value === null) return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric >= 0 ? '+' : ''}${formatCurrency(numeric)}`;
}

function formatSignedPercent(value, digits = 1) {
  if (value === undefined || value === null || value === '' || value === 'Unknown') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric >= 0 ? '+' : ''}${numeric.toFixed(digits)}%`;
}

function formatReturnValue(value) {
  if (value === undefined || value === null || value === '' || value === 'Unknown') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  const pct = Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function valueClass(value) {
  return toNumber(value) >= 0 ? 'pos' : 'neg';
}

function actionStyle(action) {
  const normalized = String(action || '').toLowerCase();
  if (normalized === 'trim' || normalized === 'review') {
    return {
      color: normalized === 'trim' ? '#ef4444' : '#f59e0b',
      background: normalized === 'trim' ? 'rgba(239, 68, 68, 0.16)' : 'rgba(245, 158, 11, 0.16)',
      border: normalized === 'trim' ? 'rgba(239, 68, 68, 0.35)' : 'rgba(245, 158, 11, 0.35)',
    };
  }
  if (normalized === 'add') {
    return {
      color: '#10b981',
      background: 'rgba(16, 185, 129, 0.16)',
      border: 'rgba(16, 185, 129, 0.35)',
    };
  }
  return {
    color: '#60a5fa',
    background: 'rgba(96, 165, 250, 0.16)',
    border: 'rgba(96, 165, 250, 0.35)',
  };
}

function ActionBadge({ action }) {
  const style = actionStyle(action);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '56px',
        padding: '0.25rem 0.55rem',
        borderRadius: '0.4rem',
        color: style.color,
        background: style.background,
        border: `1px solid ${style.border}`,
        fontSize: '0.75rem',
        fontWeight: 700,
      }}
    >
      {action || '-'}
    </span>
  );
}

function AnalysisMetricCard({ label, value, subtext, tone }) {
  const color = tone === 'positive'
    ? '#10b981'
    : tone === 'negative'
      ? '#ef4444'
      : tone === 'accent'
        ? 'var(--cyan)'
        : undefined;

  return (
    <div className="metric-card">
      <div className="metric-card-label">{label}</div>
      <div className="metric-card-value" style={{ color }}>{value}</div>
      {subtext && <div className="metric-card-sub">{subtext}</div>}
    </div>
  );
}

function formatOptionalNumber(value, digits = 1) {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return numeric.toFixed(digits);
}

function formatDays(value) {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return `${numeric.toFixed(numeric % 1 === 0 ? 0 : 1)}d`;
}

function monthLabel(month) {
  const raw = month?.month || month?.label || month?.date;
  if (!raw) return '-';
  const date = new Date(`${String(raw).slice(0, 7)}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function monthValue(month) {
  const value = month?.value ?? month?.pnl_gbp ?? month?.pnl ?? month?.value_gbp;
  return value === undefined || value === null ? '-' : formatSignedCurrency(value);
}

function AnalysisKpiCards({ data }) {
  const totalPnl = data?.total_pnl_gbp;
  const returnPct = data?.return_pct;
  const profitFactor = data?.profit_factor;
  const avgWin = data?.avg_win_gbp;
  const avgLoss = data?.avg_loss_gbp;
  const maxDrawdown = data?.max_drawdown;
  const drawdownDuration = data?.max_drawdown_duration_days;
  const profitFactorNumber = Number(profitFactor);

  const profitFactorValue = formatProfitFactor(profitFactor);

  return (
    <div className="analysis-kpi-grid">
      <AnalysisMetricCard
        label="Total P&L"
        value={totalPnl === undefined || totalPnl === null ? '-' : formatSignedCurrency(totalPnl)}
        subtext={`${formatSignedPercent(returnPct)} vs total cost`}
        tone={toNumber(totalPnl) >= 0 ? 'positive' : 'negative'}
      />
      <AnalysisMetricCard
        label="Profit Factor"
        value={profitFactorValue}
        subtext={profitFactorValue !== 'N/A' ? 'Gross profit / gross loss' : data?.profit_factor_label || 'N/A'}
        tone={profitFactorValue !== 'N/A' && profitFactorNumber >= 1 ? 'positive' : 'accent'}
      />
      <AnalysisMetricCard
        label="Avg Win / Avg Loss"
        value={`${avgWin === undefined || avgWin === null ? '-' : formatCurrency(avgWin)} / ${avgLoss === undefined || avgLoss === null ? '-' : formatCurrency(avgLoss)}`}
        subtext="Closed trades"
        tone="accent"
      />
      <AnalysisMetricCard
        label="Max Drawdown"
        value={formatSignedPercent(maxDrawdown, 1)}
        subtext={`Duration: ${formatDays(drawdownDuration)}`}
        tone="negative"
      />
    </div>
  );
}

function SnapshotMetric({ label, value, subtext, tone }) {
  return (
    <div className="analysis-snapshot-item">
      <div className="analysis-snapshot-label">{label}</div>
      <div className={`analysis-snapshot-value ${tone === 'positive' ? 'pos' : tone === 'negative' ? 'neg' : ''}`}>
        {value}
      </div>
      {subtext && <div className="analysis-snapshot-sub">{subtext}</div>}
    </div>
  );
}

function PerformanceSnapshot({ data }) {
  const snapshot = data?.performance_snapshot;
  const trend = data?.trend || snapshot?.trend || 'Flat';
  const normalizedTrend = String(trend).toLowerCase();
  const trendLabel = normalizedTrend === 'up' ? 'Up' : normalizedTrend === 'down' ? 'Down' : normalizedTrend === 'flat' ? 'Flat' : 'Unknown';
  const trendTone = normalizedTrend === 'down' ? 'negative' : normalizedTrend === 'up' ? 'positive' : 'accent';
  const best = data?.best_month || snapshot?.best_month;
  const worst = data?.worst_month || snapshot?.worst_month;

  return (
    <div className="card analysis-panel analysis-panel-wide">
      <div className="card-header">
        <span className="card-title">Performance Snapshot</span>
      </div>
      <div className="analysis-snapshot-grid">
        <SnapshotMetric
          label="Last 30 Days"
          value={snapshot ? formatSignedCurrency(snapshot.last_30d_pnl_gbp) : '-'}
          tone={toNumber(snapshot?.last_30d_pnl_gbp) >= 0 ? 'positive' : 'negative'}
        />
        <SnapshotMetric
          label="Best Month"
          value={monthValue(best)}
          subtext={monthLabel(best)}
          tone="positive"
        />
        <SnapshotMetric
          label="Worst Month"
          value={monthValue(worst)}
          subtext={monthLabel(worst)}
          tone="negative"
        />
        <SnapshotMetric
          label="Trend"
          value={trendLabel}
          subtext="Last 3 months vs previous 3"
          tone={trendTone}
        />
      </div>
    </div>
  );
}

function PnlConcentrationCard({ data }) {
  const concentration = toNumber(data?.pnl_concentration_top2);
  const displayPct = Number.isFinite(concentration) ? Math.max(0, concentration) : 0;
  const clampedPct = Math.max(0, Math.min(100, displayPct));
  const topPositions = data?.pnl_concentration?.top_positions || [];

  return (
    <div className="card analysis-panel">
      <div className="card-header">
        <span className="card-title">P&L Concentration</span>
      </div>
      <div className="analysis-concentration">
        <div
          className="analysis-donut"
          style={{
            background: `conic-gradient(#a855f7 ${clampedPct * 3.6}deg, rgba(122, 153, 184, 0.12) 0deg)`,
          }}
        >
          <div className="analysis-donut-inner">{formatPercent(displayPct, 0)}</div>
        </div>
        <div className="analysis-concentration-text">
          <div className="analysis-concentration-copy">
            of total P&L comes from top 2 positions
          </div>
          {topPositions.length > 0 && (
            <div className="analysis-concentration-tickers">
              {topPositions.slice(0, 2).join(', ')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WeightBar({ value, cap }) {
  const weight = toNumber(value);
  const fill = Math.max(0, Math.min(100, weight));
  const isOverCap = weight > cap;

  return (
    <div className="analysis-weight-cell">
      <div className="analysis-weight-track" aria-hidden="true">
        <div
          className={`analysis-weight-fill ${isOverCap ? 'overweight' : ''}`}
          style={{ width: `${fill}%` }}
        />
      </div>
      <span className={isOverCap ? 'neg' : undefined}>{formatPercent(weight, 1)}</span>
    </div>
  );
}

function TradeActivityCard({ data, tradeStats }) {
  return (
    <div className="card analysis-panel">
      <div className="card-header">
        <span className="card-title">Trade Activity</span>
      </div>
      <div className="analysis-activity-grid">
        <div>
          <div className="analysis-activity-value">{tradeStats?.total_trades ?? '-'}</div>
          <div className="analysis-activity-label">Total Trades</div>
        </div>
        <div>
          <div className="analysis-activity-value">{formatOptionalNumber(data?.avg_trades_per_week, 1)}</div>
          <div className="analysis-activity-label">Avg per week</div>
        </div>
        <div>
          <div className="analysis-activity-value">{formatDays(data?.avg_holding_time_days)}</div>
          <div className="analysis-activity-label">Avg Holding Time</div>
        </div>
        <div>
          <div className="analysis-activity-value">{tradeStats?.closed_positions ?? '-'}</div>
          <div className="analysis-activity-label">Closed Trades</div>
        </div>
      </div>
    </div>
  );
}

function AnalysisDividendSummary({ dividends, holdings }) {
  const currentYear = new Date().getFullYear();
  const thisYearDividends = (dividends || []).filter((dividend) => {
    const date = new Date(dividend.date || dividend.ex_date);
    return date.getFullYear() === currentYear;
  });
  const totalDividends = thisYearDividends.reduce(
    (sum, dividend) => sum + (Number(dividend.amount_gbp ?? dividend.amount) || 0),
    0,
  );
  const totalCost = (holdings || []).reduce(
    (sum, holding) => sum + ((Number(holding.quantity) || 0) * (Number(holding.avg_cost_gbp) || 0)),
    0,
  );
  const yieldOnCost = totalCost > 0 ? (totalDividends / totalCost) * 100 : 0;
  const tickerDividends = {};

  thisYearDividends.forEach((dividend) => {
    const ticker = dividend.ticker?.split('::')[0] || dividend.ticker;
    if (!ticker) return;
    tickerDividends[ticker] = (tickerDividends[ticker] || 0) + (Number(dividend.amount_gbp ?? dividend.amount) || 0);
  });

  const topPayers = Object.entries(tickerDividends)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="card analysis-panel analysis-dividend-card">
      <div className="card-header">
        <span className="card-title">Dividend Summary</span>
        <span className="analysis-card-eyebrow">{currentYear} YTD</span>
      </div>
      <div className="analysis-dividend-summary">
        <div className="analysis-dividend-metrics">
          <SnapshotMetric
            label="Total Received"
            value={formatCurrency(totalDividends)}
            tone="positive"
          />
          <SnapshotMetric
            label="Yield on Cost"
            value={`${yieldOnCost.toFixed(2)}%`}
            tone="positive"
          />
          <SnapshotMetric
            label="Payments"
            value={thisYearDividends.length}
            tone="positive"
          />
        </div>
        <div className="analysis-top-payers">
          <div className="analysis-top-payers-title">Top Payers</div>
          {topPayers.length > 0 ? topPayers.map(([ticker, amount]) => (
            <div className="analysis-top-payer" key={ticker}>
              <span>{ticker.toUpperCase()}</span>
              <span>{formatCurrency(amount)}</span>
            </div>
          )) : (
            <div className="analysis-top-payer muted">No dividend data available</div>
          )}
        </div>
      </div>
    </div>
  );
}

function PositionSwitcher({ positionType, setPositionType }) {
  return (
    <select
      value={positionType}
      onChange={(event) => setPositionType(event.target.value)}
      style={{ width: 'auto', minWidth: '170px' }}
    >
      {POSITION_OPTIONS.map((option) => (
        <option value={option.value} key={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function PositionTickerCell({ item, companyNameLookup }) {
  const tickerParts = item.ticker?.split('::') || [item.ticker || '-'];
  const tickerBase = tickerParts[0] || '-';
  const companyName = item.display_name || companyNameLookup[tickerBase?.toLowerCase()] || '';

  return (
    <td style={TD_STYLE}>
      <strong style={{ color: '#f4f8ff' }}>{tickerBase.toUpperCase()}</strong>
      {companyName && (
        <div
          style={{
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'var(--text-muted)',
            fontSize: '0.72rem',
            marginTop: '0.15rem',
          }}
        >
          {companyName}
        </div>
      )}
    </td>
  );
}

function AnalysisPositionTable({
  positionType,
  setPositionType,
  items,
  mergedItems,
  companyNameLookup,
  cap,
}) {
  const rows = positionType === 'closed' ? items : mergedItems;

  return (
    <div className="data-table-container analysis-table-container">
      <div className="data-table-header">
        <span className="data-table-title" style={{ whiteSpace: 'nowrap' }}>Detailed Position Analysis</span>
        <PositionSwitcher positionType={positionType} setPositionType={setPositionType} />
      </div>
      <div className="data-table-wrapper">
        <table
          className={`data-table analysis-position-table ${positionType === 'closed' ? 'is-closed' : 'is-open'}`}
          style={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <thead>
            {positionType === 'closed' ? (
              <tr>
                <th style={TH_STYLE}>Ticker</th>
                <th style={TH_CENTER_STYLE}>Broker</th>
                <th style={TH_CENTER_STYLE}>Close Date</th>
                <th style={TH_CENTER_STYLE}>Cost Basis</th>
                <th style={TH_CENTER_STYLE}>Realised P&L</th>
                <th style={TH_CENTER_STYLE}>Return %</th>
                <th style={TH_CENTER_STYLE}>P&L Contribution</th>
                <th style={TH_CENTER_STYLE}>Result</th>
              </tr>
            ) : (
              <tr>
                <th style={TH_STYLE}>Ticker</th>
                <th style={TH_CENTER_STYLE}>Broker</th>
                <th style={TH_CENTER_STYLE}>Action</th>
                <th style={TH_CENTER_STYLE}>Return</th>
                <th style={TH_CENTER_STYLE}>Current Weight</th>
                <th style={TH_CENTER_STYLE}>Suggested</th>
                <th style={TH_CENTER_STYLE}>P&L Contribution</th>
                <th style={TH_CENTER_STYLE}>Suggested Action</th>
                <th style={TH_CENTER_STYLE}>Total P&L</th>
                <th style={TH_CENTER_STYLE}>Risk</th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.length > 0 ? rows.map((item) => {
              const tickerParts = item.ticker?.split('::') || [item.ticker || '-'];
              const broker = item.broker || tickerParts[1] || '-';
              const pnl = item.pnl_gbp ?? item.realized_pnl_gbp ?? 0;
              const suggestedAction = item.suggested_action || item.action || '-';

              if (positionType === 'closed') {
                return (
                  <tr key={`${item.ticker}-${item.close_date || 'unknown'}`}>
                    <PositionTickerCell item={item} companyNameLookup={companyNameLookup} />
                    <td style={TD_CENTER_STYLE}><span className={brokerBadgeClass(broker)}>{brokerLabel(broker)}</span></td>
                    <td style={TD_CENTER_STYLE}>{formatDate(item.close_date)}</td>
                    <td style={TD_CENTER_STYLE}>{formatCurrency(item.total_cost_basis_gbp)}</td>
                    <td style={TD_CENTER_STYLE} className={valueClass(item.realized_pnl_gbp)}>
                      {formatSignedCurrency(item.realized_pnl_gbp)}
                    </td>
                    <td style={TD_CENTER_STYLE} className={valueClass(item.return_pct)}>
                      {formatSignedPercent(item.return_pct)}
                    </td>
                    <td style={TD_CENTER_STYLE}>{formatPercent(item.pnl_contribution_pct, 1)}</td>
                    <td style={TD_CENTER_STYLE}>{item.result || '-'}</td>
                  </tr>
                );
              }

              const isOverCap = toNumber(item.weight_pct) > cap;

              return (
                <tr key={item.ticker}>
                  <PositionTickerCell item={item} companyNameLookup={companyNameLookup} />
                  <td style={TD_CENTER_STYLE}><span className={brokerBadgeClass(broker)}>{brokerLabel(broker)}</span></td>
                  <td style={TD_CENTER_STYLE}>{item.action || '-'}</td>
                  <td style={TD_CENTER_STYLE} className={valueClass(item.pnl_pct)}>{formatReturnValue(item.pnl_pct)}</td>
                  <td style={TD_CENTER_STYLE}><WeightBar value={item.weight_pct} cap={cap} /></td>
                  <td style={TD_CENTER_STYLE}>{formatPercent(item.suggested_weight_pct, 1)}</td>
                  <td style={TD_CENTER_STYLE}>{formatPercent(item.pnl_contribution_pct, 1)}</td>
                  <td style={TD_CENTER_STYLE}><ActionBadge action={suggestedAction} /></td>
                  <td style={TD_CENTER_STYLE} className={valueClass(pnl)}>
                    {formatSignedCurrency(pnl)}
                  </td>
                  <td style={TD_CENTER_STYLE}>
                    <span style={{ color: isOverCap ? '#ef4444' : '#60a5fa', fontWeight: 700 }}>
                      {isOverCap ? 'High' : 'Medium'}
                    </span>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={positionType === 'closed' ? 8 : 10} style={{ textAlign: 'center', padding: '1.5rem' }} className="text-muted">
                  No analysis data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * AnalysisPage — composes the analysis view from focused subcomponents.
 *
 * Owns:
 *  - the active position type (open vs closed),
 *  - the trade-stats fetch lifecycle,
 *  - the cross-source company-name lookup,
 *  - the merged items + concentration/health derivations.
 *
 * Subcomponents handle their own presentation; no styling lives here.
 */
function AnalysisPage() {
  const {
    analysis,
    analysisClosed,
    holdings,
    isLoading,
    loadAnalysis,
    portfolio,
    summary,
    dividends,
  } = usePortfolio();

  const [positionType, setPositionType] = useState('open');
  const [tradeStats, setTradeStats] = useState(null);
  const [tradeStatsError, setTradeStatsError] = useState(null);

  // Reload analysis when the user switches tabs.
  useEffect(() => {
    loadAnalysis(positionType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionType]);

  // One-shot trade stats fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stats = await analysisApi.getTradeStats();
        if (!cancelled) {
          setTradeStats(stats);
          setTradeStatsError(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load trade stats:', error);
          setTradeStatsError(error.message || 'Failed to load');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pick the slot matching the active tab — open and closed live in separate
  // slots so the 60s auto-refresh (which only pulls 'open') cannot wipe the
  // closed-positions data.
  const currentAnalysis = positionType === 'closed' ? analysisClosed : analysis;
  const items = (currentAnalysis?.position_type === positionType) ? (currentAnalysis?.items || []) : [];
  const cap = currentAnalysis?.weight_cap_pct || analysis?.weight_cap_pct || 15;

  // Aggregate company names from holdings, summary, portfolio and the analysis
  // payload itself, keyed by the bare ticker (no broker suffix).
  const companyNameLookup = useMemo(() => {
    try {
      const lookup = {};
      const addToLookup = (ticker, name) => {
        if (!ticker || !name) return;
        const baseTicker = ticker.split('::')[0]?.toLowerCase();
        if (baseTicker && !lookup[baseTicker]) lookup[baseTicker] = name;
      };

      holdings?.forEach(h => addToLookup(h.ticker, h.display_name || h.name));
      portfolio?.holdings?.forEach(h => addToLookup(h.ticker, h.display_name || h.name));
      summary?.positions?.forEach(p => addToLookup(p.ticker, p.display_name || p.name));
      items?.forEach(it => addToLookup(it.ticker, it.display_name || it.name));

      return lookup;
    } catch (e) {
      console.error('Error building company name lookup:', e);
      return {};
    }
  }, [holdings, portfolio, summary, items]);

  // Merge analysis items with holdings (mirrors HoldingsPage logic).
  const mergedItems = useMemo(() => {
    try {
      return items.map(item => {
        const itemTickerBase = item.ticker?.split('::')[0]?.toLowerCase();
        const holding = holdings?.find(h =>
          h.ticker?.toLowerCase() === itemTickerBase ||
          h.ticker?.toLowerCase() === item.ticker?.toLowerCase()
        );
        if (!holding) return item;

        const unrealized = Number(holding.unrealized_gbp) || 0;
        const realized = Number(holding.realized_gbp) || 0;
        const pnl = unrealized + realized;
        const quantity = Number(holding.quantity) || 0;
        const avgCost = Number(holding.avg_cost_gbp) || 0;
        const cost = quantity * avgCost;
        const returnPct = cost > 0 ? pnl / cost : 0;

        return {
          ...item,
          pnl_pct: returnPct,
          pnl_gbp: pnl,
          cost_basis_gbp: cost,
        };
      });
    } catch (e) {
      console.error('Error merging items:', e);
      return items;
    }
  }, [items, holdings]);

  if (isLoading && !analysis) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="analysis-page">
      <AnalysisKpiCards data={currentAnalysis} />

      <div className="analysis-section-grid">
        <PerformanceSnapshot data={currentAnalysis} />
        <PnlConcentrationCard data={currentAnalysis} />
      </div>

      <div className="analysis-section-grid">
        <TradeActivityCard data={currentAnalysis} tradeStats={tradeStats} />
        <div className="analysis-card-compact">
          <TradeStatisticsCard tradeStats={tradeStats} />
        </div>
      </div>

      <div className="analysis-section-full">
        <AnalysisDividendSummary dividends={dividends} holdings={holdings} />
      </div>

      <AnalysisPositionTable
        positionType={positionType}
        setPositionType={setPositionType}
        items={items}
        mergedItems={mergedItems}
        companyNameLookup={companyNameLookup}
        cap={cap}
      />
    </div>
  );
}

export default AnalysisPage;
